/**
 * T129 — Expense API client + TanStack Query hooks.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { expenseKeys, expenseStatsKeys, durcharbeiteKeys } from './keys';
import { useActiveTenantId } from '../../hooks/useTenantContext';
import type { DurcharbeiteItemDto } from './durcharbeite';
import type {
  CreateExpenseInput,
  UpdateExpenseInput,
  ExpenseListQuery,
  AssignCategoryInput,
  ExpenseStatusTransitionInput,
  ExpenseDetailDto,
  ExpenseListItemDto,
  PaginatedExpenses,
} from '@armard/shared';

function buildQuery(q: Partial<ExpenseListQuery>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      for (const item of v) params.append(k, String(item));
    } else {
      params.set(k, String(v));
    }
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const expensesApi = {
  list: (q: Partial<ExpenseListQuery> = {}) =>
    api.get<PaginatedExpenses>(`/expenses${buildQuery(q)}`),
  get: (id: string) => api.get<ExpenseDetailDto>(`/expenses/${id}`),
  create: (input: CreateExpenseInput, options: { ignoreDuplicate?: boolean } = {}) =>
    api.post<ExpenseDetailDto>(
      `/expenses${options.ignoreDuplicate ? '?ignoreDuplicate=true' : ''}`,
      input
    ),
  update: (id: string, input: UpdateExpenseInput) =>
    api.put<ExpenseDetailDto>(`/expenses/${id}`, input),
  transitionStatus: (id: string, input: ExpenseStatusTransitionInput) =>
    api.post<ExpenseDetailDto>(`/expenses/${id}/status`, input),
  assignCategory: (id: string, input: AssignCategoryInput) =>
    api.post<ExpenseDetailDto>(`/expenses/${id}/assign-category`, input),
  // T-G4b — merge N belege into a primary. `versions` keyed by
  // expense-id supplies optimistic-lock for every participant.
  merge: (input: { primaryId: string; mergedIds: string[]; versions: Record<string, number> }) =>
    api.post<{ merged_count: number; ported_attachments: number }>('/expenses/merge', input),
};

export function useMergeExpenses() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: expensesApi.merge,
    onSuccess: () => qc.invalidateQueries({ queryKey: expenseKeys.all(tenantId) }),
  });
}

export function useExpenses(query: Partial<ExpenseListQuery> = {}) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: expenseKeys.list(tenantId, query),
    queryFn: () => expensesApi.list(query),
    enabled: tenantId !== null,
  });
}

export function useExpense(id: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: expenseKeys.detail(tenantId, id!),
    queryFn: () => expensesApi.get(id!),
    enabled: !!id && tenantId !== null,
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (
      args: CreateExpenseInput | { input: CreateExpenseInput; ignoreDuplicate?: boolean }
    ) => {
      if ('input' in args) {
        return expensesApi.create(args.input, { ignoreDuplicate: args.ignoreDuplicate });
      }
      return expensesApi.create(args);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: expenseKeys.all(tenantId) }),
  });
}

export function useUpdateExpense(id: string) {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (input: UpdateExpenseInput) => expensesApi.update(id, input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: expenseKeys.all(tenantId) });
      qc.setQueryData(expenseKeys.detail(tenantId, id), data);
      qc.setQueryData<DurcharbeiteItemDto>(durcharbeiteKeys.item(tenantId, id), (previous) =>
        previous ? { ...previous, expense: data } : previous
      );
    },
  });
}

export function useTransitionExpenseStatus(id: string) {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (input: ExpenseStatusTransitionInput) => expensesApi.transitionStatus(id, input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: expenseKeys.all(tenantId) });
      qc.invalidateQueries({ queryKey: expenseStatsKeys.all(tenantId) });
      qc.invalidateQueries({ queryKey: durcharbeiteKeys.queues(tenantId) });
      qc.setQueryData(expenseKeys.detail(tenantId, id), data);
      qc.setQueryData<DurcharbeiteItemDto>(durcharbeiteKeys.item(tenantId, id), (previous) =>
        previous ? { ...previous, expense: data } : previous
      );
    },
  });
}

export function useAssignCategory(id: string) {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (input: AssignCategoryInput) => expensesApi.assignCategory(id, input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: expenseKeys.all(tenantId) });
      qc.setQueryData(expenseKeys.detail(tenantId, id), data);
      qc.setQueryData<DurcharbeiteItemDto>(durcharbeiteKeys.item(tenantId, id), (previous) =>
        previous ? { ...previous, expense: data } : previous
      );
    },
  });
}

// Sprint 006 D1 — Dashboard-Ausgaben-Row.
export interface ExpenseDashboardStats {
  quartal: string;
  quartalCount: number;
  quartalGrossTotal: number;
  statusNeu: number;
  statusRueckfrage: number;
}

export function useExpenseStats(quartal: string) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: expenseStatsKeys.quartal(tenantId, quartal),
    queryFn: () => api.get<ExpenseDashboardStats>(`/expenses/stats?quartal=${quartal}`),
    enabled: tenantId !== null,
  });
}

export type { ExpenseDetailDto, ExpenseListItemDto };
